import { useState } from 'react';
import { postFetcher } from '../../functions/postFetcher';
import { ITemporarySupportResponse } from '../../types';
import BaseButton from '../primitive/BaseButton';
import BaseInput from '../primitive/BaseInput';

interface TemporarySupportLoginProps {
    onSuccess: (topicId?: number | null, jwt?: string | null) => void;
    onError?: (message: string) => void;
}

export default function TemporarySupportLogin({ onSuccess, onError }: TemporarySupportLoginProps) {
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    const handleSubmit = async () => {
        const trimmedName = name.trim();
        const trimmedPhone = phone.trim();

        if (!trimmedName) {
            setFormError('لطفاً نام را وارد کنید.');
            return;
        }

        if (!/^\d{11}$/.test(trimmedPhone)) {
            setFormError('شماره همراه باید ۱۱ رقم و فقط شامل عدد باشد.');
            return;
        }

        setIsSubmitting(true);
        setFormError('');

        try {
            const sharedWindow = globalThis as typeof globalThis & {
                chat_data?: { root?: string; nonce?: string },
                dusco_config?: Record<string, unknown>,
            }
            const root = sharedWindow.chat_data?.root ?? window.location.origin
            const apiUrl = `${root.replace(/\/$/, '')}/wp-json/dusco/v1/chat/temporary-support`

            const payload = await postFetcher<ITemporarySupportResponse>(
                apiUrl,
                { name: trimmedName, phone: trimmedPhone },
                {
                    nonce: sharedWindow.chat_data?.nonce,
                    credentials: 'include',
                }
            )

            const data = payload?.data;
            const topicId = typeof data?.topic_id === 'number' ? data.topic_id : null;
            const jwt = typeof data?.jwt === 'string' ? data.jwt : null;

            onSuccess(topicId, jwt);
        } catch (error) {
            const message = error instanceof Error ? error.message : 'درخواست شروع چت با خطا مواجه شد.';
            setFormError(message);
            onError?.(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="flex h-full w-full items-center justify-center bg-gray-100 py-10 px-4">
            <div className="w-full max-w-md rounded-xl p-6 bg-white shadow-primary-05 drop-shadow-sm">
                <div className="mb-2 text-center text-xl font-semibold">
                    شروع چت پشتیبانی
                </div>
                <div className="mb-6 text-center text-sm text-secondary-10">
                    برای شروع گفتگوی پشتیبانی، نام و شماره موبایل خود را وارد کنید.
                </div>

                <div className="flex flex-col gap-3">
                    <BaseInput
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        autoFocus
                        placeholder="نام"
                    >
                        نام
                    </BaseInput>

                    <BaseInput
                        value={phone}
                        onChange={(event) => {
                            const nextValue = event.target.value.replace(/\D/g, '').slice(0, 11);
                            setPhone(nextValue);
                        }}
                        inputMode="tel"
                        placeholder="شماره همراه"
                    >
                        شماره همراه
                    </BaseInput>

                    {formError ? (
                        <div className="text-sm text-error-00">
                            {formError}
                        </div>
                    ) : null}

                    <BaseButton
                        onClick={() => void handleSubmit()}
                        disabled={isSubmitting || !name.trim() || !phone.trim()}
                        loading={isSubmitting}
                        mode="action"
                        className="w-full"
                    >
                        شروع چت
                    </BaseButton>
                </div>
            </div>
        </div>
    );
}
